import {useEffect, useState} from 'react';
import type {Lookups} from '../types';
import {AttributeTemplateType, type IAttributeTemplate} from '../schema';

export type LookupsAllowed = Lookups;

const LOOKUP_ATTRIBUTE_TYPES = [
	AttributeTemplateType.TEXT,
	AttributeTemplateType.TEXTAREA,
	AttributeTemplateType.SELECT,
	AttributeTemplateType.NUMBER,
];

/**
 * Get valid targets/keys for lookups in sets
 */
export const useMetadataLookupsAllowed = (
	attributeTemplates: IAttributeTemplate[],
	attributeIds: number[],
	disableLookups = false,
): LookupsAllowed => {
	const [lookupsAllowed, setLookupsAllowed] = useState<LookupsAllowed>({
		keys: [],
		targets: [],
	});

	/** Set the lookup keys and targets to empty */
	const setEmpty = () => {
		setLookupsAllowed({
			keys: [],
			targets: [],
		});
	};

	useEffect(() => {
		if (disableLookups) {
			setEmpty();
			return;
		}

		// Get templates for ids
		const templates = attributeTemplates.filter(
			(attr) =>
				attributeIds.includes(attr.attributeid) &&
				LOOKUP_ATTRIBUTE_TYPES.includes(attr.type),
		);

		// Get all key related attributes
		const keys = templates.filter((attr) => !!attr.keyDatasets.length);
		// Get all target related attributes
		const targets = templates.filter((attr) => !!attr.targetDatasets.length);

		// Nohting here
		if (!keys.length || !targets.length) {
			setEmpty();
			return;
		}

		// Get the attribute ids that are keys with target and targets with keys
		// we will not support lookups on keys that have no targets and vice versa
		setLookupsAllowed({
			keys: keys
				.filter((attr) =>
					attr.keyDatasets.some((keyId: number) =>
						templates.some((attr) => attr.targetDatasets.includes(keyId)),
					),
				)
				.map((attr) => attr.attributeid),
			targets: targets
				.filter((attr) =>
					attr.targetDatasets.some((targetId: number) =>
						templates.some((attr) => attr.keyDatasets.includes(targetId)),
					),
				)
				.map((attr) => attr.attributeid),
		});
	}, [disableLookups, attributeIds, attributeTemplates]);

	return lookupsAllowed;
};

export default useMetadataLookupsAllowed;
